import {
  AnchorButton as BlueprintAnchorButton,
  AnchorButtonProps as BlueprintAnchorTabProps,
} from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';

import {
  InteractiveBadge,
  InteractiveBadgeProps,
} from 'bones/design-system/components/badge/InteractiveBadge';
import {
  Icon,
  IconComponentType,
  IconWrapperProps,
} from 'bones/design-system/components/icons/Icon';

const BADGE_SIZE_BY_TAB_SIZE: Record<
  TabProps['size'],
  InteractiveBadgeProps['size']
> = {
  lg: 'md',
  md: 'md',
  sm: 'xs',
  xs: 'xs',
};

const ICON_SIZE_BY_TAB_SIZE: Record<
  TabProps['size'],
  IconWrapperProps['size']
> = {
  lg: 'md',
  md: 'md',
  sm: 'sm',
  xs: 'sm',
};

export type TabProps = React.AnchorHTMLAttributes<HTMLAnchorElement> &
  Omit<
    BlueprintAnchorTabProps,
    | 'alignText'
    | 'fill'
    | 'icon'
    | 'intent'
    | 'large'
    | 'loading'
    | 'minimal'
    | 'outlined'
    | 'rightIcon'
    | 'small'
    | 'text'
  > & {
    'data-testid'?: never;
    badgeProps?: Omit<
      InteractiveBadgeProps,
      'active' | 'disabled' | 'selected' | 'size' | 'variant'
    >;
    leftIcon?: IconComponentType;
    leftIconProps?: Omit<IconWrapperProps, 'size'> &
      Partial<Pick<IconWrapperProps, 'size'>>;
    rightIcon?: IconComponentType;
    rightIconProps?: Omit<IconWrapperProps, 'size'> &
      Partial<Pick<IconWrapperProps, 'size'>>;
    selected?: boolean;
    size: 'lg' | 'md' | 'sm' | 'xs';
    tabIndex?: number;
    testId?: string;
  };

export const Tab: React.VFC<TabProps> = ({
  active,
  badgeProps,
  children,
  className,
  disabled,
  leftIcon,
  leftIconProps,
  rightIcon,
  rightIconProps,
  selected,
  size,
  testId,
  ...props
}) => (
  <BlueprintAnchorButton
    active={active}
    className={classNames(className, 'vb-tab', `vb-tab-size--${size}`, {
      'vb-tab--icon': !children,
      'vb-tab-state--selected': selected,
    })}
    data-testid={testId}
    disabled={disabled}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  >
    {leftIcon ? (
      <Icon
        IconComponent={leftIcon}
        size={ICON_SIZE_BY_TAB_SIZE[size]}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...leftIconProps}
      />
    ) : null}
    {children}
    {badgeProps ? (
      <InteractiveBadge
        active={active}
        disabled={disabled}
        size={BADGE_SIZE_BY_TAB_SIZE[size]}
        variant="light"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...badgeProps}
      />
    ) : null}
    {rightIcon ? (
      <Icon
        IconComponent={rightIcon}
        size={ICON_SIZE_BY_TAB_SIZE[size]}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rightIconProps}
      />
    ) : null}
  </BlueprintAnchorButton>
);
